import React, { FocusEvent, MutableRefObject, useCallback, useEffect } from "react";
import { Button, ButtonAppearance, Buttons } from "capitalrx-components-2.0/lib/general";
import * as Color from "capitalrx-components-2.0/lib/general/Color";
import styled from "styled-components";

const StyledMenuButton = styled(Button)`
  font-family: "Source Sans Pro", sans-serif;
  font-size: 14px !important;
`;

const MenuButtonAppearance: ButtonAppearance = {
  ...Buttons.Functional.Tertiary.Compact,

  normal: {
    ...Buttons.Functional.Tertiary.Compact.normal,
    foregroundColor: Color.DarkGrey["500"],
  },
  hover: {
    ...Buttons.Functional.Tertiary.Compact.hover,
    foregroundColor: Color.Turqoise["500"],
  },
  pressed: {
    ...Buttons.Functional.Tertiary.Compact.pressed,
    foregroundColor: Color.DarkGrey["500"],
  },
};

const ExpandedMenuButtonAppearance: ButtonAppearance = {
  ...MenuButtonAppearance,
  normal: {
    ...Buttons.Functional.Tertiary.Large.normal,
    foregroundColor: Color.Turqoise["500"],
  },
};

interface MenuButtonProps {
  label: string;
  expanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  menuRef: MutableRefObject<HTMLDivElement>;
  buttonContainerRef: MutableRefObject<HTMLDivElement>;
}

export const FullWidthDropdownMenuButton = ({ label, expanded, setExpanded, menuRef, buttonContainerRef }: MenuButtonProps) => {
  const clickHandler = useCallback(() => {
    setExpanded((previousExpanded) => !previousExpanded);

    if (buttonContainerRef.current) {
      buttonContainerRef.current.focus(); // Ensures the button gets the focus.
    }
  }, []);

  const mouseDownHandler = useCallback((ev: React.MouseEvent): void => {
    ev.preventDefault();
  }, []);

  const blurHandler = useCallback((ev: FocusEvent): void => {
    const { relatedTarget } = ev;
    if (relatedTarget) {
      if (relatedTarget === buttonContainerRef?.current) return;
      if (relatedTarget === menuRef?.current || (menuRef.current && menuRef.current.contains(relatedTarget as Node))) return;
    }

    setExpanded(false);
  }, []);

  useEffect(() => {
    function listener(): void {
      setExpanded(false);
    }

    function handleLinkClick(event: MouseEvent) {
      const target = event.target as HTMLElement;
      const anchor = target.closest("a");

      if (anchor && anchor.target === "_blank" && menuRef.current && menuRef.current.contains(anchor)) {
        setExpanded(false);
      }
    }

    if (expanded) {
      document.addEventListener("scroll", listener);
      window.addEventListener("resize", listener);
      document.addEventListener("click", handleLinkClick);
    }

    return (): void => {
      document.removeEventListener("scroll", listener);
      window.removeEventListener("resize", listener);
      document.removeEventListener("click", handleLinkClick);
    };
  }, [expanded, menuRef]);

  useEffect(() => {
    setExpanded(false);
  }, [location]);

  return (
    <div
      className="menu-button"
      ref={buttonContainerRef}
      onClickCapture={clickHandler}
      onBlurCapture={blurHandler}
      onMouseDown={mouseDownHandler}
      tabIndex={1}
    >
      <StyledMenuButton
        appearance={expanded ? ExpandedMenuButtonAppearance : MenuButtonAppearance}
        tabIndex={-1}
        iconPosition="right"
        icon={expanded ? "chevron-up" : "chevron-down"}
      >
        {label}
      </StyledMenuButton>
    </div>
  );
};
